import { Api } from './api';
import { NotificationType } from '@common/notificationTypes';
import { TypedEventEmitter } from './typedEventEmitter';
import { DataCache } from './dataCache';
import { Config } from '../config';
import { Logger } from '../../../common/logger';
import { FirebaseService } from '../services/firebaseService';
import { JobManager } from './jobManager';
import { CandidateManager } from './candidateManager';
import { GatedSendManager } from './gatedSendManager';
import { SettingsManager } from './settingsManager';
import { AdminManager } from './adminManager';
import { WorkspaceManager } from './workspaceManager';
import { AppEventType, } from './appEvents';
import { MessageTransport, ScheduledMessageStatus, nowUtc, } from '@common/clientServerApi';
import { mergeNewestChatPage } from '@common/chatPaging';
import { CommMetricsCache } from '@common/commMetricsCache';
import { resolveCandidateCommunicationsConfig, resolveQualificationWorkflow } from '@common/candidateCommunications';
import { platformChannelAvailability } from '@common/candidateChannels';
import { buildDefaultAntiGhostingConfig } from '@common/antiGhostingDefaults';
/** An id for this tab (one per page load): its notification subscription is its own. */
function newTabId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
        return crypto.randomUUID();
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}
/** Back-off before re-requesting teammates' communication settings after a failed load. */
const TEAM_COMMUNICATIONS_RETRY_MS = 60000;
/**
 * Main application class for UI actions and data synchronization
 * 1. Transforms UI actions into HTTP requests
 * 2. Keeps UI in sync with backend data via page-specific fetching   results: {
        candidateId: CandidateId;
        success: boolean;
        proposedTimeSlots?: TimeSlot[];
        error?: string;
    }[];
 * 3. Handles token expiration and requests refresh from AuthService
 * 4. Maintains cache and state without destruction on token refresh
 */
import { RecruiterMcpManager } from './recruiterMcpManager';
import { RecruiterVoiceClient } from './recruiterVoiceClient';
export class App {
    constructor(userRole) {
        this.recruiterMcp = new RecruiterMcpManager(() => this.getApi());
        this.recruiterVoice = new RecruiterVoiceClient(() => this.getApi());
        /** Conversations being loaded, so views opening the same one share the request. */
        this.chatLoads = new Map();
        /** Per conversation whose newest page is loading: messages changed here meanwhile (they keep that state). */
        this.chatChangesDuringLoad = new Map();
        this.api = null;
        this.authStateUnsubscribe = null;
        /** Teammates' workspace communication settings (their jobs follow them); null until first needed. */
        this.teammateCommunications = null;
        /** The workspace's communication report counters; loaded when first needed (`loadCommMetrics`). */
        this.commMetrics = new CommMetricsCache(() => this.makeApiCallWithRetry(() => this.getApi().getCommMetrics()), (snapshot) => this.eventEmitter.emit(AppEventType.COMM_METRICS_UPDATED, { snapshot }));
        this.teammateCommunicationsRequest = null;
        this.teammateCommunicationsRetryAtMs = 0;
        /** The workspace's Meta Lead Ads integration; null until first needed (`loadMetaLeadAds`). */
        this.metaLeadAds = null;
        this.metaLeadAdsLoad = null;
        /** Bumped on sign-out so a load still in flight can't fill the next user's cache. */
        this.teammateCommunicationsSession = 0;
        // Loading state tracking
        this.loadingStates = new Map();
        /** Prevents duplicate sign-in when onAuthStateChanged fires multiple times */
        this.activeSignInUid = null;
        /**
         * Bumped on every sign-in and sign-out: a response that comes back in a later session (signed out meanwhile, or
         * out and in again) is dropped, its error too, so it can't fill or fail the session it doesn't belong to.
         */
        this.signInSession = 0;
        this.tabId = newTabId();
        this.eventEmitter = new TypedEventEmitter();
        this.dataCache = new DataCache();
        this.config = new Config();
        this.logger = new Logger('App');
        this.userRole = userRole;
        this.firebaseService = new FirebaseService(this.logger.createChild('FirebaseService'));
        this.authService = this.firebaseService.createAuthService();
        this.telemetryService = this.firebaseService.createTelemetryService();
        this.notificationService = this.firebaseService.createNotificationService();
        // Forward notifications directly to handler
        this.notificationService.onNotification((notification) => {
            this.handleNotification(notification);
        });
        // Durable activity inbox (§6.4) — forward changes to ACTIVITY_UPDATED so the UI re-renders.
        this.activityInboxService = this.firebaseService.createActivityInboxService();
        this.activityInboxService.onChange(() => {
            this.eventEmitter.emit(AppEventType.ACTIVITY_UPDATED, {
                items: this.activityInboxService.getItems(),
                unreadCount: this.activityInboxService.getUnreadCount(),
                timestamp: new Date(),
            });
        });
        const getApi = () => { this.ensureAuthenticated(); return this.api; };
        const ensureAuthenticated = () => this.ensureAuthenticated();
        const makeApiCallWithRetry = (apiCall) => this.makeApiCallWithRetry(apiCall);
        const handleError = (context, error) => this.handleError(context, error);
        const toError = (error) => this.toError(error);
        this.jobManager = new JobManager({
            getApi, dataCache: this.dataCache, eventEmitter: this.eventEmitter,
            logger: this.logger, telemetryService: this.telemetryService,
            ensureAuthenticated, makeApiCallWithRetry, handleError, toError,
        });
        this.candidateManager = new CandidateManager({
            getApi, dataCache: this.dataCache, eventEmitter: this.eventEmitter,
            logger: this.logger, telemetryService: this.telemetryService,
            ensureAuthenticated, makeApiCallWithRetry, handleError, toError,
        });
        this.gatedSendManager = new GatedSendManager({
            getApi, dataCache: this.dataCache, eventEmitter: this.eventEmitter,
            candidateManager: this.candidateManager,
            makeApiCallWithRetry,
        });
        this.settingsManager = new SettingsManager({
            getApi, dataCache: this.dataCache,
            ensureAuthenticated, makeApiCallWithRetry, handleError, toError,
        });
        this.adminManager = new AdminManager({
            getApi, dataCache: this.dataCache, eventEmitter: this.eventEmitter,
            logger: this.logger,
            ensureAuthenticated, makeApiCallWithRetry, handleError, toError,
        });
        this.workspaceManager = new WorkspaceManager({
            getApi, dataCache: this.dataCache, eventEmitter: this.eventEmitter,
            ensureAuthenticated, makeApiCallWithRetry, handleError, toError,
        });
        // Set up auth state listener immediately (sync)
        this.setupAuthStateListener();
        this.logger.info('App initialized successfully');
    }
    /**
     * Login with Google and set user role
     * @returns true if sign-in was initiated, false if user cancelled
     */
    async signIn() {
        try {
            this.logger.info('Starting login process with role:', this.userRole);
            // Delegate to AuthService - returns null if user cancelled
            const user = await this.authService.signInWithGoogle();
            if (user) {
                this.logger.info('Login process initiated');
                return true;
            }
            // User cancelled - return false so UI can react
            return false;
        }
        catch (error) {
            this.logger.error('Login failed:', error);
            this.handleError('login', this.toError(error));
            throw error;
        }
    }
    /**
     * Sign out the user
     */
    async signOut() {
        try {
            // Delegate to AuthService
            await this.authService.signOut();
            this.logger.info('Sign out process initiated');
        }
        catch (error) {
            this.logger.error('Sign out failed:', error);
            this.handleError('signOut', this.toError(error));
            throw error;
        }
    }
    /**
     * Shutdown the client and stop all services
     */
    shutdown() {
        this.notificationService.stop();
        this.activityInboxService.stop();
        this.eventEmitter.removeAllListeners();
        // Clean up auth state listener
        if (this.authStateUnsubscribe) {
            this.authStateUnsubscribe();
            this.authStateUnsubscribe = null;
        }
        this.api = null;
    }
    /**
     * Check if user is authenticated
     */
    isAuthenticated() {
        return this.authService.isAuthenticated() && this.api !== null;
    }
    /**
     * Get current user from AuthService
     */
    getCurrentUser() {
        return this.authService.getCurrentUser();
    }
    getCurrentUserData() {
        return this.dataCache.getUserData();
    }
    /**
     * The current recruiter's authoritative render timezone (`Profile.timezone`).
     * Only valid in authenticated views where user data is loaded; throws otherwise —
     * silently guessing the browser zone is the exact bug the UtcMs migration removes.
     */
    getCurrentUserTimezone() {
        const userData = this.dataCache.getUserData();
        if (!userData) {
            throw new Error('getCurrentUserTimezone() called with no loaded user data');
        }
        return userData.profile.timezone;
    }
    // ── Activity feed (durable inbox, §6.4) ────────────────────────────────
    /** Durable activity-feed items for the current user, newest-first. */
    getActivityItems() {
        return this.activityInboxService.getItems();
    }
    /** Count of unread activity items. */
    getActivityUnreadCount() {
        return this.activityInboxService.getUnreadCount();
    }
    /** Mark all unread activity read ("Mark all as read"). No-op if none unread. */
    async markActivityRead() {
        if (this.getActivityUnreadCount() === 0)
            return;
        try {
            await this.makeApiCallWithRetry(() => this.api.markActivityRead());
        }
        catch (error) {
            this.logger.warn('Failed to mark activity read:', this.toError(error));
        }
    }
    /** Set read/unread on specific activity items (open-marks-read + manual toggle). */
    async setActivityRead(ids, read) {
        if (ids.length === 0)
            return;
        try {
            await this.makeApiCallWithRetry(() => this.api.markActivityRead(ids, read));
        }
        catch (error) {
            this.logger.warn('Failed to set activity read state:', this.toError(error));
        }
    }
    /** Dispatch an activity CTA command (§5). Returns true on success, false on failure. */
    async executeActivityAction(activityId, command) {
        try {
            await this.makeApiCallWithRetry(() => this.api.executeActivityAction(activityId, command));
            return true;
        }
        catch (error) {
            this.handleError('executeActivityAction', this.toError(error));
            return false;
        }
    }
    /** Send a trial WhatsApp message to the recruiter's own number (no retry — avoids double-send). */
    async sendWhatsAppTrial(phoneE164) {
        this.ensureAuthenticated();
        return this.api.sendWhatsAppTrial(phoneE164);
    }
    getOrganization() {
        return this.dataCache.getOrganization();
    }
    getPendingInvitations() {
        return this.dataCache.getPendingInvitations();
    }
    getCachedSettings() {
        return this.dataCache.getSettings();
    }
    // ===============================
    // Event Management (Observer Pattern)
    // ===============================
    /**
     * Subscribe to events
     */
    on(event, listener) {
        this.eventEmitter.on(event, listener);
    }
    /**
     * Unsubscribe from events
     */
    off(event, listener) {
        this.eventEmitter.off(event, listener);
    }
    /**
     * Subscribe to event once
     */
    once(event, listener) {
        this.eventEmitter.once(event, listener);
    }
    /**
     * Emit an event
     */
    emit(event, ...args) {
        this.eventEmitter.emit(event, ...args);
    }
    // ===============================
    // Profile & User Data Operations
    // ===============================
    async updateConsent() {
        return this.workspaceManager.updateConsent();
    }
    async updateProfile(profileUpdates) {
        return this.workspaceManager.updateProfile(profileUpdates);
    }
    async getDataSummary() {
        return this.workspaceManager.getDataSummary();
    }
    async deleteAllUserData(confirmationPhrase, reason) {
        return this.workspaceManager.deleteAllUserData(confirmationPhrase, reason);
    }
    // ===============================
    // Loading State Management
    // ===============================
    /**
     * Check if a specific operation is loading
     */
    isLoading(operation) {
        return this.loadingStates.get(operation) || false;
    }
    /**
     * Get all current loading states
     */
    getLoadingStates() {
        return Object.fromEntries(this.loadingStates);
    }
    /**
     * Check if initial data (from login) has already been loaded.
     * Useful for components that mount after the JOBS_LOADED event has fired.
     */
    isInitialDataLoaded() {
        return this.dataCache.getLastUpdateTimestamp() > 0;
    }
    /**
     * Get the underlying API instance for direct access (admin operations)
     */
    getApi() {
        this.ensureAuthenticated();
        return this.api;
    }
    // ===============================
    // Job Operations (delegated to JobManager)
    // ===============================
    getJobsFromCache() {
        return this.jobManager.getFromCache();
    }
    async refreshJobs() {
        return this.jobManager.refresh();
    }
    async getJob(id) {
        return this.jobManager.getById(id);
    }
    async createJob(jobData) {
        return this.jobManager.create(jobData);
    }
    async createJobFromUrl(url) {
        return this.jobManager.createFromUrl(url);
    }
    async createJobFromText(text) {
        return this.jobManager.createFromText(text);
    }
    async convertPendingSession(sessionId) {
        return this.jobManager.convertPendingSession(sessionId);
    }
    async parseJobDescription(jobDescriptionText) {
        return this.jobManager.parseDescription(jobDescriptionText);
    }
    async updateJob(id, data) {
        return this.jobManager.update(id, data);
    }
    async deleteJob(id) {
        return this.jobManager.delete(id);
    }
    async publishJob(jobId, publish) {
        return this.jobManager.publish(jobId, publish);
    }
    async optimizeJobScoring(jobId, jobDescription, additionalRequirements, answers) {
        return this.jobManager.optimizeScoring(jobId, jobDescription, additionalRequirements, answers);
    }
    // ===============================
    // Candidate Operations (delegated to CandidateManager)
    // ===============================
    getCandidatesFromCache() {
        return this.candidateManager.getFromCache();
    }
    async getCandidate(id) {
        return this.candidateManager.getById(id);
    }
    /**
     * Synchronous cache read — returns the anti-ghosting follow-up messages
     * for a candidate (optionally scoped to a single job), ordered by
     * `scheduledFor` ascending. Hydrated by the combined login response and
     * patched live via `SCHEDULED_MESSAGE(S)_UPDATED` notifications. **No
     * network round-trip.**
     */
    getScheduledMessages(candidateId, jobId) {
        return this.dataCache.getScheduledMessagesFor(candidateId, jobId);
    }
    /** Edit a Pending follow-up message body (recruiter override). */
    async updateScheduledMessageBody(messageId, body) {
        const { message } = await this.makeApiCallWithRetry(() => this.api.updateScheduledMessage(messageId, { body }));
        // Patch local cache + emit so subscribers re-render without waiting
        // for the round-trip notification.
        this.dataCache.upsertScheduledMessage(message);
        this.eventEmitter.emit(AppEventType.SCHEDULED_MESSAGE_UPDATED, { message });
        return message;
    }
    /** Cancel a Pending follow-up message. Sent/Delivered/Failed messages are immutable. */
    async cancelScheduledMessage(messageId) {
        const { message } = await this.makeApiCallWithRetry(() => this.api.updateScheduledMessage(messageId, {
            status: ScheduledMessageStatus.Cancelled,
        }));
        this.dataCache.upsertScheduledMessage(message);
        this.eventEmitter.emit(AppEventType.SCHEDULED_MESSAGE_UPDATED, { message });
        return message;
    }
    // ─── Admin: global scheduled-message control ─────────────────────────────
    //
    // These methods power the admin app's global control panel. Each patches
    // the local cache from the response delta and emits the same events as
    // recruiter-side mutations so any visible table re-renders. The server
    // also broadcasts notifications scoped to the message's `orgId` so the
    // affected recruiter's cache stays in sync.
    /** Admin: read every scheduled message currently in the cache, sorted ascending by `scheduledFor`. */
    getAllScheduledMessages() {
        return this.dataCache.getAllScheduledMessages();
    }
    /** Admin: edit body / reschedule / cancel any scheduled message regardless of org. */
    async adminUpdateScheduledMessage(messageId, patch) {
        const { message } = await this.makeApiCallWithRetry(() => this.api.adminUpdateScheduledMessage(messageId, patch));
        this.dataCache.upsertScheduledMessage(message);
        this.eventEmitter.emit(AppEventType.SCHEDULED_MESSAGE_UPDATED, { message });
        return message;
    }
    /** Admin: dispatch a Pending message immediately. */
    async adminSendScheduledMessageNow(messageId) {
        const { message } = await this.makeApiCallWithRetry(() => this.api.adminSendScheduledMessageNow(messageId));
        this.dataCache.upsertScheduledMessage(message);
        this.eventEmitter.emit(AppEventType.SCHEDULED_MESSAGE_UPDATED, { message });
        return message;
    }
    /** Admin: hard-delete the message. Cache is patched optimistically; the
     *  server also broadcasts a SCHEDULED_MESSAGE_DELETED notification so
     *  recruiters see it removed too. */
    async adminDeleteScheduledMessage(messageId) {
        // Capture identity for the event before we drop from cache.
        const existing = this.dataCache.getAllScheduledMessages().find((m) => m.id === messageId);
        await this.makeApiCallWithRetry(() => this.api.adminDeleteScheduledMessage(messageId));
        this.dataCache.removeScheduledMessage(messageId);
        if (existing) {
            this.eventEmitter.emit(AppEventType.SCHEDULED_MESSAGE_DELETED, {
                id: existing.id,
                candidateId: existing.candidateId,
                jobId: existing.jobId,
            });
        }
    }
    /**
     * Mark a candidate's interview as complete. Server flips state to PostInterviewThanked
     * and schedules any InterviewEnd-anchored follow-ups. The updated candidate is patched
     * into the local cache; SCHEDULED_MESSAGE_UPDATED notifications carry the new messages.
     */
    async markInterviewComplete(candidateId, jobId, interviewEndTimeUtc) {
        const { candidate } = await this.makeApiCallWithRetry(() => this.api.markInterviewComplete(candidateId, { jobId, interviewEndTimeUtc }));
        this.dataCache.updateCandidate(candidate);
        return candidate;
    }
    /**
     * Send a recruiter-decision touchpoint immediately (e.g. offer / rejection).
     * Server flips state to DecisionSent and queues the message via the standard
     * ScheduledMessage path. Returns the updated candidate.
     */
    async sendDecisionMessage(candidateId, jobId, target, customBody) {
        const { candidate } = await this.makeApiCallWithRetry(() => this.api.sendDecisionMessage(candidateId, {
            jobId, ...target, customBody,
        }));
        this.dataCache.updateCandidate(candidate);
        return candidate;
    }
    /** Render the decision touchpoint body server-side without sending. Used by the draft dialog. */
    async previewDecisionMessage(candidateId, jobId, target) {
        return this.makeApiCallWithRetry(() => this.api.previewDecisionMessage(candidateId, { jobId, ...target }));
    }
    /** Update the recruiter's workspace-default follow-up sequence. Patches local userData. */
    async updateWorkspaceAntiGhostingConfig(config) {
        this.ensureAuthenticated();
        const { config: saved } = await this.makeApiCallWithRetry(() => this.api.updateWorkspaceAntiGhostingConfig({ config }));
        const userData = this.dataCache.getUserData();
        if (userData) {
            const updated = {
                ...userData,
                workspaceSettings: { ...userData.workspaceSettings, antiGhostingConfig: saved },
            };
            this.dataCache.updateUserData(updated);
            this.eventEmitter.emit(AppEventType.USERDATA_UPDATED, { userData: updated, timestamp: new Date() });
        }
        return saved;
    }
    /** Set or clear the per-job follow-up override. Pass null to revert to workspace default. */
    async updateJobAntiGhostingConfig(jobId, config) {
        this.ensureAuthenticated();
        const { job } = await this.makeApiCallWithRetry(() => this.api.updateJobAntiGhostingConfig(jobId, { config }));
        this.dataCache.updateJob(job);
        return job;
    }
    // ─── Per-candidate reminders (Phase B) ──────────────────────────────────
    //
    // Reminders are a per-candidate snapshot of `JobApplication.reminders[]`.
    // The snapshot is delivered with the candidate (login response) and
    // patched live via CANDIDATE_UPDATED notifications — **no GET round-trip**.
    // Mutations return a delta (new reminders array + any pending message
    // that was scheduled or cancelled); we patch the local cache and emit
    // SCHEDULED_MESSAGE_UPDATED so the timeline picks up the change.
    /** Add a recruiter-defined reminder for one candidate. */
    async addCandidateReminder(candidateId, jobId, input) {
        this.ensureAuthenticated();
        const result = await this.makeApiCallWithRetry(() => this.api.addCandidateReminder(candidateId, jobId, input));
        this.patchCandidateReminders(candidateId, jobId, result.reminders);
        if (result.scheduledMessage) {
            this.eventEmitter.emit(AppEventType.SCHEDULED_MESSAGE_UPDATED, { message: result.scheduledMessage });
        }
        return result;
    }
    /** Edit a per-candidate reminder. Cancels any in-flight pending message and reschedules. */
    async updateCandidateReminder(candidateId, jobId, reminderId, patch) {
        this.ensureAuthenticated();
        const result = await this.makeApiCallWithRetry(() => this.api.updateCandidateReminder(candidateId, jobId, reminderId, patch));
        this.patchCandidateReminders(candidateId, jobId, result.reminders);
        if (result.cancelledMessage) {
            this.eventEmitter.emit(AppEventType.SCHEDULED_MESSAGE_UPDATED, { message: result.cancelledMessage });
        }
        if (result.scheduledMessage) {
            this.eventEmitter.emit(AppEventType.SCHEDULED_MESSAGE_UPDATED, { message: result.scheduledMessage });
        }
        return result;
    }
    /** Delete a per-candidate reminder. Cancels any in-flight pending message. */
    async deleteCandidateReminder(candidateId, jobId, reminderId) {
        this.ensureAuthenticated();
        const result = await this.makeApiCallWithRetry(() => this.api.deleteCandidateReminder(candidateId, jobId, reminderId));
        this.patchCandidateReminders(candidateId, jobId, result.reminders);
        if (result.cancelledMessage) {
            this.eventEmitter.emit(AppEventType.SCHEDULED_MESSAGE_UPDATED, { message: result.cancelledMessage });
        }
        return result;
    }
    /** Internal: patch `application.reminders` on the cached candidate. */
    patchCandidateReminders(candidateId, jobId, reminders) {
        this.patchCachedApplication(candidateId, jobId, (application) => ({ ...application, reminders }));
    }
    /**
     * Replace one cached application with `patch(current)` and notify listeners.
     * `patch` returns null to keep the cache as is (e.g. a newer notification already arrived).
     */
    patchCachedApplication(candidateId, jobId, patch) {
        const cached = this.dataCache.getCandidates().find((c) => c.id === candidateId);
        const application = cached?.applications?.[jobId];
        if (!cached || !application)
            return;
        const next = patch(application);
        if (!next)
            return;
        this.replaceCachedCandidate({ ...cached, applications: { ...cached.applications, [jobId]: next } });
    }
    replaceCachedCandidate(candidate) {
        this.dataCache.updateCandidate(candidate);
        this.eventEmitter.emit(AppEventType.CANDIDATE_UPDATED, { candidate, timestamp: new Date() });
    }
    // ─── No-response follow-ups ─────────────────────────────────────────────
    //
    // The sequence lives on `JobApplication.responseExpectation` and is patched
    // live by CANDIDATE_UPDATED notifications; these commands patch the returned delta.
    /** Deliver the next scheduled follow-up now. */
    async sendResponseFollowUpNow(candidateId, jobId) {
        const api = this.getApi();
        const result = await this.makeApiCallWithRetry(() => api.sendResponseFollowUpNow(candidateId, jobId));
        this.patchResponseExpectation(candidateId, jobId, result.responseExpectation);
        return result.responseExpectation;
    }
    /** Skip the next scheduled follow-up (skipping the last one stops the sequence). */
    async skipResponseFollowUp(candidateId, jobId) {
        const api = this.getApi();
        const result = await this.makeApiCallWithRetry(() => api.skipResponseFollowUp(candidateId, jobId));
        this.patchResponseExpectation(candidateId, jobId, result.responseExpectation);
        return result.responseExpectation;
    }
    /** Stop the remaining follow-ups for this application. */
    async stopResponseFollowUps(candidateId, jobId) {
        const api = this.getApi();
        const result = await this.makeApiCallWithRetry(() => api.stopResponseFollowUps(candidateId, jobId));
        this.patchResponseExpectation(candidateId, jobId, result.responseExpectation);
        return result.responseExpectation;
    }
    patchResponseExpectation(candidateId, jobId, responseExpectation) {
        this.patchCachedApplication(candidateId, jobId, (application) => {
            // A newer notification may already carry a later state of the same sequence.
            const current = application.responseExpectation;
            if (current?.id === responseExpectation.id && current.updatedAtUtc > responseExpectation.updatedAtUtc) {
                return null;
            }
            return { ...application, responseExpectation };
        });
    }
    // ─── Candidate communications controls ──────────────────────────────────
    /** Whether the candidate-communications platform capability is on (rollout flag / kill switch). */
    isCandidateCommunicationsEnabled() {
        return this.getCachedSettings()?.features.enableCandidateCommunications === true;
    }
    /** Whether an admin has switched on the Meta Lead Ads integration for the platform. */
    isMetaLeadAdsEnabled() {
        return this.getCachedSettings()?.features.enableMetaLeadAds === true;
    }
    /** Whether an admin has switched on recruiter calling for the platform. */
    isRecruiterCallingEnabled() {
        return this.getCachedSettings()?.features.enableRecruiterCalling === true;
    }
    /** Channels the platform can deliver on (WhatsApp once an admin switches it on; no email provider yet). */
    getChannelAvailability() {
        return platformChannelAvailability({
            enableCandidateWhatsApp: this.getCachedSettings()?.features.enableCandidateWhatsApp === true,
        });
    }
    /** Workspace communication settings, with defaults for sections older workspaces never saved. */
    getCandidateCommunicationsConfig() {
        const stored = this.getCurrentUserData()?.workspaceSettings?.candidateCommunications;
        return resolveCandidateCommunicationsConfig(stored);
    }
    /**
     * The workspace communication settings a job owned by `ownerId` follows — automation runs on the
     * job owner's settings. Your own are always live; a teammate's load once on first use (defaults
     * until then, then TEAM_COMMUNICATIONS_LOADED).
     */
    getCandidateCommunicationsConfigFor(ownerId) {
        if (this.getCurrentUserData()?.id === ownerId)
            return this.getCandidateCommunicationsConfig();
        return this.teammateCommunicationsFor(ownerId)?.candidateCommunications
            ?? resolveCandidateCommunicationsConfig(undefined);
    }
    /** The workspace reminder sequence for jobs owned by `ownerId`, before any per-job override. */
    getAntiGhostingDefaultFor(ownerId) {
        if (this.getCurrentUserData()?.id === ownerId) {
            return this.getCurrentUserData()?.workspaceSettings?.antiGhostingConfig ?? buildDefaultAntiGhostingConfig();
        }
        return this.teammateCommunicationsFor(ownerId)?.antiGhostingConfig ?? buildDefaultAntiGhostingConfig();
    }
    /** Display name of an org member, e.g. whose settings a teammate's job follows. */
    getOrgMemberName(userId) {
        return this.getOrganization()?.members.find((member) => member.userId === userId)?.name ?? null;
    }
    /** The screening workflow a job actually uses: its own complete override, else its owner's default. */
    getEffectiveQualificationWorkflow(job) {
        return resolveQualificationWorkflow(job.qualificationWorkflow, this.getCandidateCommunicationsConfigFor(job.userId).qualification);
    }
    teammateCommunicationsFor(ownerId) {
        const known = this.teammateCommunications?.get(ownerId);
        if (known)
            return known;
        // First use, or an owner who joined after the last load.
        void this.loadTeammateCommunications();
        return null;
    }
    loadTeammateCommunications() {
        if (this.teammateCommunicationsRequest)
            return this.teammateCommunicationsRequest;
        // Throttled: an owner the server doesn't list (e.g. a former member) must not refetch every render.
        if (Date.now() < this.teammateCommunicationsRetryAtMs)
            return Promise.resolve();
        this.teammateCommunicationsRetryAtMs = Date.now() + TEAM_COMMUNICATIONS_RETRY_MS;
        const request = this.fetchTeammateCommunications(this.teammateCommunicationsSession)
            .finally(() => {
            if (this.teammateCommunicationsRequest === request)
                this.teammateCommunicationsRequest = null;
        });
        this.teammateCommunicationsRequest = request;
        return request;
    }
    async fetchTeammateCommunications(session) {
        try {
            const { members } = await this.getApi().getOrgCommunicationsDefaults();
            // Signed out (or in as someone else) meanwhile: these belong to the previous session.
            if (session !== this.teammateCommunicationsSession)
                return;
            this.teammateCommunications = new Map(members.map((member) => [member.userId, member]));
            this.eventEmitter.emit(AppEventType.TEAM_COMMUNICATIONS_LOADED, { members, timestamp: new Date() });
        }
        catch (error) {
            // Teammates' jobs show default settings until a later render retries.
            this.logger.warn('Failed to load teammates\' communication settings', error);
        }
    }
    /** The workspace's communication report counters, or null until they're loaded (`loadCommMetrics`). */
    getCommMetrics() {
        return this.commMetrics.get();
    }
    /**
     * Load the communication report counters (one load at a time); COMM_METRICS_UPDATED when they're in. Live
     * updates add to them from then on. Rejects when the load fails.
     */
    loadCommMetrics() {
        return this.commMetrics.load();
    }
    // ─── Meta Lead Ads integration ──────────────────────────────────────────
    /** The workspace's Meta Lead Ads integration, or null until it's loaded (`loadMetaLeadAds`). */
    getMetaLeadAds() {
        return this.metaLeadAds;
    }
    /** Load the integration again (one load at a time); META_LEAD_ADS_UPDATED when it's in. */
    loadMetaLeadAds() {
        if (this.metaLeadAdsLoad)
            return this.metaLeadAdsLoad;
        const session = this.signInSession;
        const load = this.makeApiCallWithRetry(() => this.getApi().getMetaLeadAds())
            .then((snapshot) => {
            if (this.signInSession === session)
                this.setMetaLeadAds(snapshot);
            return snapshot;
        })
            .finally(() => {
            if (this.metaLeadAdsLoad === load)
                this.metaLeadAdsLoad = null;
        });
        this.metaLeadAdsLoad = load;
        return load;
    }
    /** The Meta sign-in page to send the browser to; it comes back to Settings → Integrations. */
    async startMetaLeadAdsConnect() {
        const { url } = await this.makeApiCallWithRetry(() => this.getApi().connectMetaLeadAds());
        return url;
    }
    async listMetaLeadForms(pageId) {
        const { forms } = await this.makeApiCallWithRetry(() => this.getApi().listMetaLeadForms(pageId));
        return forms;
    }
    async getMetaLeadForm(pageId, formId) {
        const { form } = await this.makeApiCallWithRetry(() => this.getApi().getMetaLeadForm(pageId, formId));
        return form;
    }
    /** Connect a form to a job (or change how its answers map). */
    async saveMetaLeadFormMapping(pageId, formId, request) {
        const { mapping } = await this.makeApiCallWithRetry(() => this.getApi().saveMetaLeadFormMapping(pageId, formId, request));
        this.patchMetaLeadAds((snapshot) => ({
            ...snapshot,
            mappings: [...snapshot.mappings.filter((existing) => existing.id !== mapping.id), mapping]
                .sort((a, b) => a.formName.localeCompare(b.formName)),
        }));
        return mapping;
    }
    async deleteMetaLeadFormMapping(pageId, formId) {
        const { mappingId } = await this.makeApiCallWithRetry(() => this.getApi().deleteMetaLeadFormMapping(pageId, formId));
        this.patchMetaLeadAds((snapshot) => ({
            ...snapshot,
            mappings: snapshot.mappings.filter((mapping) => mapping.id !== mappingId),
        }));
    }
    /** Import a lead again, once what stopped it is fixed. */
    async retryMetaLead(leadId) {
        const { lead } = await this.makeApiCallWithRetry(() => this.getApi().retryMetaLead(leadId));
        this.patchMetaLeadAds((snapshot) => ({
            ...snapshot,
            recentLeads: snapshot.recentLeads.map((existing) => (existing.id === lead.id ? lead : existing)),
        }));
        return lead;
    }
    /** Disconnect Meta: every form, and Stanor's access. */
    async disconnectMetaLeadAds() {
        await this.makeApiCallWithRetry(() => this.getApi().disconnectMetaLeadAds());
        this.patchMetaLeadAds((snapshot) => ({ ...snapshot, connection: null, mappings: [] }));
    }
    setMetaLeadAds(snapshot) {
        this.metaLeadAds = snapshot;
        this.eventEmitter.emit(AppEventType.META_LEAD_ADS_UPDATED, { snapshot });
    }
    patchMetaLeadAds(patch) {
        if (this.metaLeadAds)
            this.setMetaLeadAds(patch(this.metaLeadAds));
    }
    /** Re-send a welcome text that failed or was held back by a pause. */
    async retryApplicationAcknowledgement(candidateId, jobId) {
        const api = this.getApi();
        const result = await this.makeApiCallWithRetry(() => api.retryApplicationAcknowledgement(candidateId, jobId));
        const { applicationAcknowledgement } = result;
        this.patchCachedApplication(candidateId, jobId, (application) => {
            // The task may already have reported a later state (e.g. Sent) by notification.
            const current = application.applicationAcknowledgement;
            if (current && current.updatedAtUtc > applicationAcknowledgement.updatedAtUtc)
                return null;
            return { ...application, applicationAcknowledgement };
        });
        return applicationAcknowledgement;
    }
    /** Pause or resume automatic outreach (screening, follow-ups, reminders) to one candidate. */
    async setCandidateAutomation(candidateId, command) {
        const api = this.getApi();
        const { candidate } = await this.makeApiCallWithRetry(() => api.setCandidateAutomation(candidateId, command));
        const merged = this.mergeCandidateSnapshot(candidate);
        this.replaceCachedCandidate(merged);
        return merged;
    }
    /**
     * A full candidate from an HTTP response: take it, except for any application the cache
     * already holds a later version of (per-application last-write-wins, as for notifications).
     */
    mergeCandidateSnapshot(candidate) {
        const cached = this.dataCache.getCandidates().find((c) => c.id === candidate.id);
        if (!cached)
            return candidate;
        const applications = { ...candidate.applications };
        for (const [jobId, cachedApplication] of Object.entries(cached.applications ?? {})) {
            const incoming = applications[jobId];
            if (incoming && (cachedApplication.updatedAtUtc ?? 0) > (incoming.updatedAtUtc ?? 0)) {
                applications[jobId] = cachedApplication;
            }
        }
        return { ...candidate, applications };
    }
    /** Customize a job's screening workflow, or pass null to use the workspace default again. */
    async updateJobQualificationWorkflow(jobId, qualificationWorkflow) {
        const api = this.getApi();
        const { job } = await this.makeApiCallWithRetry(() => api.updateJobQualificationWorkflow(jobId, { qualificationWorkflow }));
        return this.applyUpdatedJob(job);
    }
    /** Give a job its own welcome text, or (null) the workspace default again. */
    async updateJobApplicationAcknowledgement(jobId, applicationAcknowledgement) {
        const api = this.getApi();
        const { job } = await this.makeApiCallWithRetry(() => api.updateJobApplicationAcknowledgement(jobId, { applicationAcknowledgement }));
        return this.applyUpdatedJob(job);
    }
    /** Give a job its own no-reply follow-ups, or (null) the workspace default again. */
    async updateJobNoResponseFollowUps(jobId, noResponseFollowUps) {
        const api = this.getApi();
        const { job } = await this.makeApiCallWithRetry(() => api.updateJobNoResponseFollowUps(jobId, { noResponseFollowUps }));
        return this.applyUpdatedJob(job);
    }
    /** The job as the server saved it, in the cache and to subscribers. */
    applyUpdatedJob(job) {
        this.dataCache.updateJob(job);
        this.eventEmitter.emit(AppEventType.JOB_UPDATED, { job, timestamp: new Date() });
        return job;
    }
    async createCandidate(candidateData, resumeFile, jobId) {
        return this.candidateManager.create(candidateData, resumeFile, jobId);
    }
    async createCandidatesFromResumes(resumeFiles, jobId) {
        return this.candidateManager.createFromResumes(resumeFiles, jobId);
    }
    async startCandidateQuestions(candidateId, jobId) {
        return this.candidateManager.startQuestions(candidateId, jobId);
    }
    // ───────────────────────────────────────────────────────────────────
    // Gated dispatch (delegated to GatedSendManager)
    //
    // Every recruiter-facing method that may surface a UI gate
    // (consent / phone-verify / upgrade / confirm-questions / confirm-slots)
    // routes through GatedSendManager, which owns the preflight, error→
    // action mapping and cache patching. App keeps thin delegators.
    // ───────────────────────────────────────────────────────────────────
    /** See {@link GatedSendManager.sendSms}. */
    async sendSms(candidateIds, jobId, transport = MessageTransport.Sms) {
        return this.gatedSendManager.sendSms(candidateIds, jobId, transport);
    }
    async sendMessageToCandidate(candidateId, jobId, transport = MessageTransport.Sms) {
        return this.candidateManager.sendMessage(candidateId, jobId, transport);
    }
    /** See {@link GatedSendManager.batchSendMessages}. */
    async batchSendMessages(candidateIds, jobId, transport = MessageTransport.Sms) {
        return this.gatedSendManager.batchSendMessages(candidateIds, jobId, transport);
    }
    /** See {@link GatedSendManager.grantCandidateConsent}. */
    async grantCandidateConsent(candidateIds) {
        return this.gatedSendManager.grantCandidateConsent(candidateIds);
    }
    /** See {@link GatedSendManager.batchInviteToInterview}. */
    async batchInviteToInterview(candidateIds, jobId, saveSlots, slots, scheduleDirectly, touchpointOverrides, reminders) {
        return this.gatedSendManager.batchInviteToInterview(candidateIds, jobId, saveSlots, slots, scheduleDirectly, touchpointOverrides, reminders);
    }
    /** See {@link GatedSendManager.proposeAvailability}. */
    async proposeAvailability(candidateIds, jobId, args) {
        return this.gatedSendManager.proposeAvailability(candidateIds, jobId, args);
    }
    /** See {@link GatedSendManager.scheduleInterview}. */
    async scheduleInterview(candidateId, jobId, args) {
        return this.gatedSendManager.scheduleInterview(candidateId, jobId, args);
    }
    async getCandidateQuestions(candidateId, jobId) {
        return this.candidateManager.getQuestions(candidateId, jobId);
    }
    async inviteCandidateToInterview(candidateId, jobId) {
        return this.candidateManager.inviteToInterview(candidateId, jobId);
    }
    async inviteToInterview(candidateId, jobId) {
        return this.candidateManager.inviteToInterview(candidateId, jobId);
    }
    async cancelInterview(eventId, reason) {
        return this.candidateManager.cancelInterview(eventId, reason);
    }
    /** "Ask candidate to repick" — cancels current slot + sends re-engagement SMS. */
    async requestReschedule(candidateId, jobId, reason) {
        return this.candidateManager.requestReschedule(candidateId, jobId, reason);
    }
    /** "Set new time" — full reschedule to a new specific slot. */
    async rescheduleInterview(candidateId, jobId, newSlot, rescheduleMessage, opts) {
        return this.candidateManager.rescheduleInterview(candidateId, jobId, newSlot, rescheduleMessage, opts);
    }
    async acceptCandidate(candidateId, jobId) {
        return this.candidateManager.acceptCandidate(candidateId, jobId);
    }
    async rejectCandidate(candidateId, jobId, reason) {
        return this.candidateManager.rejectCandidate(candidateId, jobId, reason);
    }
    async restoreCandidate(candidateId, jobId) {
        return this.candidateManager.restoreCandidate(candidateId, jobId);
    }
    async sendToOffer(candidateId, jobId) {
        return this.candidateManager.sendToOffer(candidateId, jobId);
    }
    /**
     * Save interview slot preferences at the given scope.
     * - global: saves WeeklySlotTemplate to workspace settings
     * - job: saves WeeklySlotTemplate to the job
     * - candidate: saves TimeSlot[] to the candidate's job application
     */
    async saveInterviewSlots(params) {
        const { scope, jobId, candidateIds, template, timeSlots } = params;
        if (scope === 'global' && template) {
            await this.updateProfile({ workspaceSettings: { interviewSlotTemplate: template } });
        }
        else if (scope === 'job' && template) {
            const job = this.dataCache.getJobs().find(j => j.id === jobId);
            if (job) {
                await this.updateJob(jobId, {
                    description: job.description,
                    interviewSlotTemplate: template,
                });
            }
        }
        else if (scope === 'candidate' && timeSlots && candidateIds?.length) {
            const api = this.getApi();
            await Promise.allSettled(candidateIds.map(async (cId) => {
                const res = await api.updateCandidateJobData({
                    candidateId: cId,
                    jobId,
                    updates: { interviewSlots: timeSlots },
                });
                if (res.candidate) {
                    this.dataCache.updateCandidate(res.candidate);
                    this.eventEmitter.emit(AppEventType.CANDIDATE_UPDATED, {
                        candidate: res.candidate,
                        timestamp: new Date(),
                    });
                }
            }));
        }
    }
    // ===============================
    // Chat Operations
    // ===============================
    /**
     * Return cached messages for instant UI render. Returns [] if not yet fetched.
     */
    getCachedChatMessages(chatId) {
        return this.dataCache.getChatMessages(chatId);
    }
    /**
     * Load a conversation's newest page into the cache and return it. Views opening the same chat at once
     * share one request.
     */
    async getChatMessages(chatId) {
        this.ensureAuthenticated();
        const inFlight = this.chatLoads.get(chatId);
        if (inFlight)
            return inFlight;
        const load = this.loadNewestChatMessages(chatId);
        this.chatLoads.set(chatId, load);
        try {
            return await load;
        }
        finally {
            this.chatLoads.delete(chatId);
        }
    }
    async loadNewestChatMessages(chatId) {
        // Messages that change here while the page loads (a reply, a delivery update, a send) keep that state.
        const touched = new Set();
        this.chatChangesDuringLoad.set(chatId, touched);
        const session = this.signInSession;
        try {
            const { messages, earlierCursor } = await this.makeApiCallWithRetry(() => this.api.getMessages(chatId, null));
            // Signed out meanwhile: the conversation isn't this session's to show.
            if (this.signInSession !== session)
                return [];
            const cached = this.dataCache.getChatMessages(chatId);
            const merged = mergeNewestChatPage(cached, messages, touched, this.dataCache.isChatLoaded(chatId));
            this.dataCache.setChatMessages(chatId, merged.messages);
            // Earlier messages shown are kept (the page joined onto them), and so is where to read on from.
            this.dataCache.setChatEarlierCursor(chatId, merged.keptEarlier ? this.dataCache.getChatEarlierCursor(chatId) : earlierCursor);
            this.eventEmitter.emit(AppEventType.CHAT_MESSAGE_RECEIVED, { chatId, messages: merged.messages, earlierLoaded: false });
            return merged.messages;
        }
        finally {
            if (this.chatChangesDuringLoad.get(chatId) === touched)
                this.chatChangesDuringLoad.delete(chatId);
        }
    }
    /** Whether a conversation's newest messages were loaded. */
    isChatLoaded(chatId) {
        return this.dataCache.isChatLoaded(chatId);
    }
    /** Whether a loaded conversation has messages before those loaded ("Show earlier messages"). */
    hasEarlierChatMessages(chatId) {
        return this.dataCache.getChatEarlierCursor(chatId) !== null;
    }
    /** Load the page before the earliest loaded message of a conversation and put it in front. */
    async loadEarlierChatMessages(chatId) {
        this.ensureAuthenticated();
        const cursor = this.dataCache.getChatEarlierCursor(chatId);
        if (cursor === null)
            return;
        const { messages, earlierCursor } = await this.makeApiCallWithRetry(() => this.api.getMessages(chatId, cursor));
        // The newest page reloaded meanwhile and set a new place: putting this page in front of it could leave a
        // gap. Dropping it loses nothing; Show earlier messages loads from the new place.
        if (this.dataCache.getChatEarlierCursor(chatId) !== cursor)
            return;
        const loaded = this.dataCache.getChatMessages(chatId);
        const known = new Set(loaded.map((message) => message.id));
        const merged = [...messages.filter((message) => !known.has(message.id)), ...loaded];
        this.dataCache.setChatMessages(chatId, merged);
        this.dataCache.setChatEarlierCursor(chatId, earlierCursor);
        this.eventEmitter.emit(AppEventType.CHAT_MESSAGE_RECEIVED, { chatId, messages: merged, earlierLoaded: true });
    }
    /**
     * Fetch all chats (admin only).
     */
    async getAllChats(windowMs) {
        this.ensureAuthenticated();
        const { chats } = await this.makeApiCallWithRetry(() => this.api.getAllChats(windowMs));
        return chats;
    }
    /**
     * Send a message: immediately show it as 'pending', fire the API call,
     * then update to 'sent' when the server confirms.
     */
    sendChatMessage(chatId, text) {
        this.ensureAuthenticated();
        const user = this.getCurrentUser();
        // 1. Create message with 'pending' status and add to cache immediately
        const pendingMessage = {
            id: `pending-${Date.now()}`,
            text,
            timestampUtc: nowUtc(),
            senderId: user.uid,
            senderName: user.displayName || user.email || 'You',
            status: 'pending',
        };
        const cached = this.dataCache.getChatMessages(chatId);
        const withPending = [...cached, pendingMessage];
        this.chatChangesDuringLoad.get(chatId)?.add(pendingMessage.id);
        this.dataCache.setChatMessages(chatId, withPending);
        this.eventEmitter.emit(AppEventType.CHAT_MESSAGE_RECEIVED, { chatId, messages: withPending, earlierLoaded: false });
        // 2. Async send — update to 'sent' on confirmation, 'failed' on error
        this.makeApiCallWithRetry(() => this.api.sendMessage(chatId, text)).then(({ message }) => {
            this.chatChangesDuringLoad.get(chatId)?.add(message.id);
            const current = this.dataCache.getChatMessages(chatId);
            const hasPending = current.some((m) => m.id === pendingMessage.id);
            // The delivery-outcome notification can arrive first and add the message itself.
            const updated = hasPending
                ? current
                    .filter((m) => m.id !== message.id)
                    .map((m) => (m.id === pendingMessage.id ? message : m))
                : current.some((m) => m.id === message.id) ? current : [...current, message];
            this.dataCache.setChatMessages(chatId, updated);
            this.eventEmitter.emit(AppEventType.CHAT_MESSAGE_RECEIVED, { chatId, messages: updated, earlierLoaded: false });
        }).catch(() => {
            const current = this.dataCache.getChatMessages(chatId);
            const updated = current.map(m => m.id === pendingMessage.id ? { ...m, status: 'failed' } : m);
            this.dataCache.setChatMessages(chatId, updated);
            this.eventEmitter.emit(AppEventType.CHAT_MESSAGE_RECEIVED, { chatId, messages: updated, earlierLoaded: false });
        });
        return withPending;
    }
    // ===============================
    // Settings Operations (delegated to SettingsManager)
    // ===============================
    async getSettings() {
        return this.settingsManager.get();
    }
    async updateSettings(settings) {
        return this.settingsManager.update(settings);
    }
    // ===============================
    // Organization Management
    // ===============================
    async getOrgDetails() {
        this.ensureAuthenticated();
        try {
            return await this.makeApiCallWithRetry(() => this.api.getOrg());
        }
        catch (error) {
            this.handleError('getOrgDetails', this.toError(error));
            throw error;
        }
    }
    async updateOrg(updates) {
        this.ensureAuthenticated();
        try {
            const result = await this.makeApiCallWithRetry(() => this.api.updateOrg(updates));
            this.dataCache.updateData({ organization: result.organization, timestamp: Date.now() });
            this.eventEmitter.emit(AppEventType.ORG_UPDATED, { organization: result.organization, timestamp: new Date() });
            return result.organization;
        }
        catch (error) {
            this.handleError('updateOrg', this.toError(error));
            throw error;
        }
    }
    async inviteMember(email, role) {
        this.ensureAuthenticated();
        try {
            const result = await this.makeApiCallWithRetry(() => this.api.inviteMember(email, role));
            const next = [...this.dataCache.getPendingInvitations(), result.invitation];
            this.dataCache.setPendingInvitations(next);
            this.eventEmitter.emit(AppEventType.ORG_INVITATIONS_UPDATED, { invitations: next });
            return result.invitation;
        }
        catch (error) {
            this.handleError('inviteMember', this.toError(error));
            throw error;
        }
    }
    async cancelInvitation(inviteId) {
        this.ensureAuthenticated();
        try {
            await this.makeApiCallWithRetry(() => this.api.cancelInvitation(inviteId));
            const next = this.dataCache.getPendingInvitations().filter((inv) => inv.id !== inviteId);
            this.dataCache.setPendingInvitations(next);
            this.eventEmitter.emit(AppEventType.ORG_INVITATIONS_UPDATED, { invitations: next });
        }
        catch (error) {
            this.handleError('cancelInvitation', this.toError(error));
            throw error;
        }
    }
    async acceptInvitation(inviteId) {
        this.ensureAuthenticated();
        try {
            const result = await this.makeApiCallWithRetry(() => this.api.acceptInvitation(inviteId));
            this.dataCache.updateData({ organization: result.organization, timestamp: Date.now() });
            this.eventEmitter.emit(AppEventType.ORG_UPDATED, { organization: result.organization, timestamp: new Date() });
            return result.organization;
        }
        catch (error) {
            this.handleError('acceptInvitation', this.toError(error));
            throw error;
        }
    }
    async declineInvitation(inviteId) {
        this.ensureAuthenticated();
        try {
            await this.makeApiCallWithRetry(() => this.api.declineInvitation(inviteId));
        }
        catch (error) {
            this.handleError('declineInvitation', this.toError(error));
            throw error;
        }
    }
    async removeMember(userId) {
        this.ensureAuthenticated();
        try {
            const result = await this.makeApiCallWithRetry(() => this.api.removeMember(userId));
            this.dataCache.updateData({ organization: result.organization, timestamp: Date.now() });
            this.eventEmitter.emit(AppEventType.ORG_UPDATED, { organization: result.organization, timestamp: new Date() });
            return result.organization;
        }
        catch (error) {
            this.handleError('removeMember', this.toError(error));
            throw error;
        }
    }
    async transferOwnership(targetUserId) {
        this.ensureAuthenticated();
        try {
            const result = await this.makeApiCallWithRetry(() => this.api.transferOwnership(targetUserId));
            this.dataCache.updateData({ organization: result.organization, timestamp: Date.now() });
            this.eventEmitter.emit(AppEventType.ORG_UPDATED, { organization: result.organization, timestamp: new Date() });
            return result.organization;
        }
        catch (error) {
            this.handleError('transferOwnership', this.toError(error));
            throw error;
        }
    }
    async leaveOrg() {
        this.ensureAuthenticated();
        try {
            await this.makeApiCallWithRetry(() => this.api.leaveOrg());
        }
        catch (error) {
            this.handleError('leaveOrg', this.toError(error));
            throw error;
        }
    }
    // ===============================
    // Stripe Billing
    // ===============================
    async createCheckoutSession(planId, billingPeriod, successUrl, cancelUrl) {
        this.ensureAuthenticated();
        return this.makeApiCallWithRetry(() => this.api.createCheckoutSession({ planId, billingPeriod, successUrl, cancelUrl }));
    }
    async createPortalSession(returnUrl) {
        this.ensureAuthenticated();
        return this.makeApiCallWithRetry(() => this.api.createPortalSession({ returnUrl }));
    }
    // ── Admin Stripe / Coupons ──
    async adminListCoupons() {
        this.ensureAuthenticated();
        return this.makeApiCallWithRetry(() => this.api.adminListCoupons());
    }
    async adminCreateCoupon(body) {
        this.ensureAuthenticated();
        return this.makeApiCallWithRetry(() => this.api.adminCreateCoupon(body));
    }
    async adminDeleteCoupon(id) {
        this.ensureAuthenticated();
        return this.makeApiCallWithRetry(() => this.api.adminDeleteCoupon(id));
    }
    async adminListPromotionCodes() {
        this.ensureAuthenticated();
        return this.makeApiCallWithRetry(() => this.api.adminListPromotionCodes());
    }
    async adminCreatePromotionCode(body) {
        this.ensureAuthenticated();
        return this.makeApiCallWithRetry(() => this.api.adminCreatePromotionCode(body));
    }
    async adminUpdatePromotionCode(id, active) {
        this.ensureAuthenticated();
        return this.makeApiCallWithRetry(() => this.api.adminUpdatePromotionCode(id, active));
    }
    async adminListPayments(limit = 50) {
        this.ensureAuthenticated();
        return this.makeApiCallWithRetry(() => this.api.adminListPayments(limit));
    }
    async adminRefundPayment(body) {
        this.ensureAuthenticated();
        return this.makeApiCallWithRetry(() => this.api.adminRefundPayment(body));
    }
    // ===============================
    // Phone Verification (Free-tier SMS gating)
    // ===============================
    /**
     * Pre-flight: ask the server whether we can proceed with verifying `phone`.
     * Returns `{ allowed: true }` or `{ allowed: false, reason, code }`.
     */
    async startPhoneVerification(phone) {
        this.ensureAuthenticated();
        try {
            return await this.makeApiCallWithRetry(() => this.api.startPhoneVerification(phone));
        }
        catch (error) {
            this.handleError('startPhoneVerification', this.toError(error));
            throw error;
        }
    }
    /**
     * Submit the refreshed Firebase ID token (with `phone` identity claim) to
     * persist the verified number. Patches local userData + organization caches.
     */
    async confirmPhoneVerification(phone, firebasePhoneIdToken) {
        this.ensureAuthenticated();
        try {
            const result = await this.makeApiCallWithRetry(() => this.api.confirmPhoneVerification(phone, firebasePhoneIdToken));
            const userData = this.dataCache.getUserData();
            if (userData) {
                const updated = { ...userData, profile: result.profile };
                this.dataCache.updateUserData(updated);
                this.eventEmitter.emit(AppEventType.USERDATA_UPDATED, { userData: updated, timestamp: new Date() });
            }
            this.dataCache.updateData({ organization: result.organization, timestamp: Date.now() });
            this.eventEmitter.emit(AppEventType.ORG_UPDATED, { organization: result.organization, timestamp: new Date() });
        }
        catch (error) {
            this.handleError('confirmPhoneVerification', this.toError(error));
            throw error;
        }
    }
    /** Remove the caller's verified phone (server + cache). */
    async removeVerifiedPhone() {
        this.ensureAuthenticated();
        try {
            const result = await this.makeApiCallWithRetry(() => this.api.removeVerifiedPhone());
            const userData = this.dataCache.getUserData();
            if (userData) {
                const updated = { ...userData, profile: result.profile };
                this.dataCache.updateUserData(updated);
                this.eventEmitter.emit(AppEventType.USERDATA_UPDATED, { userData: updated, timestamp: new Date() });
            }
            this.dataCache.updateData({ organization: result.organization, timestamp: Date.now() });
            this.eventEmitter.emit(AppEventType.ORG_UPDATED, { organization: result.organization, timestamp: new Date() });
        }
        catch (error) {
            this.handleError('removeVerifiedPhone', this.toError(error));
            throw error;
        }
    }
    /**
     * Underlying Firebase Auth instance (for `linkWithPhoneNumber` /
     * `RecaptchaVerifier`). Used only by the phone-verification modal.
     */
    getFirebaseAuth() {
        return this.authService.getFirebaseAuth();
    }
    // ===============================
    // Admin User Management (delegated to AdminManager)
    // ===============================
    async getAllUsers(windowMs) {
        return this.adminManager.getAllUsers(windowMs);
    }
    async getAllOrgs(windowMs) {
        return this.adminManager.getAllOrgs(windowMs);
    }
    async updateOrgSubscription(orgId, patch) {
        return this.adminManager.updateOrgSubscription(orgId, patch);
    }
    async adminMoveUser(userId, body) {
        return this.adminManager.moveUser(userId, body);
    }
    async adminRemoveVerifiedPhone(orgId, userId) {
        return this.adminManager.removeVerifiedPhone(orgId, userId);
    }
    async adminUnlinkUserPhoneProvider(userId) {
        return this.adminManager.unlinkUserPhoneProvider(userId);
    }
    async getPhoneVerificationStats(windowMs) {
        return this.adminManager.getPhoneVerificationStats(windowMs);
    }
    async getTwilioStatus(limit) {
        return this.adminManager.getTwilioStatus(limit);
    }
    /** Admin: every workspace's communication counters for the last `days` days (the platform view). */
    async getAdminCommMetrics(days) {
        return this.adminManager.getCommPlatformMetrics(days);
    }
    /** Admin: a page of recorded communication outcomes, newest first. */
    async getAdminCommEvents(filter) {
        return this.adminManager.getCommEvents(filter);
    }
    /** Admin: list every WhatsApp template in the Twilio account. */
    async listWhatsAppTemplates(forceRefresh = false) {
        return this.adminManager.listWhatsAppTemplates(forceRefresh);
    }
    /** Admin: send a WhatsApp template to any number for verification. */
    async sendWhatsAppTemplateTest(contentSid, payload) {
        return this.adminManager.sendWhatsAppTemplateTest(contentSid, payload);
    }
    async getAdminUserSnapshot(userId) {
        return this.adminManager.getUserSnapshot(userId);
    }
    async createAllowedUser(name, email, role) {
        return this.adminManager.createAllowedUser(name, email, role);
    }
    async updateUser(userId, userData) {
        return this.adminManager.updateUser(userId, userData);
    }
    async deleteUser(userId) {
        return this.adminManager.deleteUser(userId);
    }
    async getAllowedUsers(windowMs) {
        return this.adminManager.getAllowedUsers(windowMs);
    }
    async removeAllowedUser(email) {
        return this.adminManager.removeAllowedUser(email);
    }
    async getWaitlist(windowMs) {
        return this.adminManager.getWaitlist(windowMs);
    }
    async getAuditLeads(windowMs) {
        return this.adminManager.getAuditLeads(windowMs);
    }
    async getQuizLeads(windowMs) {
        return this.adminManager.getQuizLeads(windowMs);
    }
    async getFeedback(windowMs) {
        return this.adminManager.getFeedback(windowMs);
    }
    async updateUserPrivileged(userId, updates, reason) {
        return this.adminManager.updateUserPrivileged(userId, updates, reason);
    }
    async fetchUserData(userId) {
        return this.adminManager.fetchUserData(userId);
    }
    // ===============================
    // Candidate Management (delegated to CandidateManager)
    // ===============================
    async deleteCandidates(candidateIds) {
        return this.candidateManager.delete(candidateIds);
    }
    async resolveCandidateConflict(candidateId, jobId, action) {
        return this.candidateManager.resolveConflict(candidateId, jobId, action);
    }
    async adminDeleteCandidates(candidateIds) {
        this.ensureAuthenticated();
        const response = await this.makeApiCallWithRetry(() => this.api.adminDeleteCandidates(candidateIds));
        for (const id of response.deleted) {
            this.candidateManager.removeFromCache(id);
        }
        if (response.deleted.length > 0) {
            this.eventEmitter.emit(AppEventType.CANDIDATE_DELETED, { candidateIds: response.deleted, timestamp: new Date() });
        }
        return response;
    }
    async scoreCandidate(candidateId, jobId) {
        return this.candidateManager.score(candidateId, jobId);
    }
    async associateCandidateWithJob(params) {
        return this.candidateManager.associateWithJob(params.candidateId, params.jobId, params.reason);
    }
    /**
     * Talent-pool rediscovery (Phase 0 sourcing): rank the org's existing
     * candidates by fit to a job. Read-only — no cache mutation. Adding a match
     * is done via `associateCandidateWithJob`, which auto-scores the candidate.
     */
    async getTalentPoolMatches(jobId, limit) {
        return this.makeApiCallWithRetry(() => this.api.getTalentPoolMatches(jobId, { limit }));
    }
    // ===============================
    // Utility Methods
    // ===============================
    /**
     * Clear all caches
     */
    clearCache() {
        this.dataCache.clear();
    }
    // ===============================
    // Private Methods
    // ===============================
    /**
     * Set up auth state listener to monitor AuthService changes
     */
    setupAuthStateListener() {
        this.authStateUnsubscribe = this.authService.onAuthStateChanged(async (authState) => {
            try {
                if (!authState.isAuthenticated || !authState.user || !authState.token) {
                    this.logger.info('User signed out or auth state invalid');
                    await this.handleUserSignOut();
                    return;
                }
                this.logger.info('User authenticated:', authState.user.email);
                await this.handleUserSignIn(authState.user, authState.token);
            }
            catch (error) {
                this.logger.error('Error in auth state change:', error);
                this.handleError('authStateChange', this.toError(error));
            }
        });
    }
    /**
     * Handle user sign in from AuthService
     * Uses OPTIMISTIC UI: Shows app immediately, backend auth runs in background
     */
    async handleUserSignIn(user, token) {
        if (this.activeSignInUid === user.uid) {
            if (this.api)
                this.api.updateToken(token);
            return;
        }
        this.activeSignInUid = user.uid;
        const session = ++this.signInSession;
        this.recruiterMcp.clear();
        try {
            this.logger.info(`🔐 Starting user sign-in process for: ${user.email} with role: ${this.userRole}`);
            // Initialize API with new token
            this.api = new Api(this.config.apiUrl, token);
            // OPTIMISTIC: Emit login event IMMEDIATELY after Firebase auth
            // Don't wait for backend auth - show the app right away
            this.eventEmitter.emit(AppEventType.USER_LOGGED_IN, {
                user: {
                    id: user.uid,
                    name: user.displayName || 'Anonymous',
                    email: user.email || 'Not provided',
                    photoUrl: user.photoURL || '',
                },
                timestamp: new Date()
            });
            // Set telemetry user context immediately
            this.telemetryService.setUserId(user.uid);
            this.telemetryService.trackUserLogin({
                userRole: this.userRole,
                authMethod: 'google',
            });
            // Start Notification Service (Firestore Listener + backend subscription)
            this.notificationService.start(user.uid, {
                // Through the retry: a tab open for hours holds an expired token until a call refreshes it.
                unsubscribe: () => this.makeApiCallWithRetry(() => this.api.unsubscribeNotifications(this.tabId)),
                unsubscribeSync: () => this.api?.unsubscribeNotificationsSync(this.tabId),
                heartbeat: async () => (await this.makeApiCallWithRetry(() => this.api.notificationsHeartbeat(this.tabId))).lapsed,
                resync: () => this.reloadData(),
            });
            // Start durable activity-inbox listener (§6.4).
            this.activityInboxService.start(user.uid);
            this.logger.info('✅ Firebase auth completed, UI shown. Starting backend auth in background...');
            // Run backend auth and data fetch in background (non-blocking)
            this.handleBackendAuthAndDataFetch(session).catch(error => {
                if (this.signInSession !== session)
                    return;
                this.logger.error('Background auth/data fetch failed:', error);
                // Emit error event so UI can show access denied
                this.handleError('login', this.toError(error));
            });
        }
        catch (error) {
            this.logger.error('Error handling user sign in:', error);
            throw error;
        }
    }
    /**
     * Background task: Backend authentication + data fetching
     * Uses combined login+data fetch for a single round-trip
     */
    async handleBackendAuthAndDataFetch(session) {
        this.logger.info('🔐 Background: Starting combined auth + data fetch...');
        const startTime = Date.now();
        // Combined auth + data fetch in a single request
        const loginResponse = await this.handleBackendAuthenticationWithData();
        if (this.signInSession !== session) {
            this.logger.info('Sign-in data arrived after its session ended; dropped');
            return;
        }
        // Process the combined response
        if (loginResponse) {
            this.processLoginDataResponse(loginResponse, startTime);
        }
        this.logger.info(`✅ Background: Combined auth + data completed in ${Date.now() - startTime}ms`);
    }
    /**
     * Handle user sign out from AuthService
     */
    async handleUserSignOut() {
        this.activeSignInUid = null;
        this.signInSession++;
        this.recruiterMcp.clear();
        this.telemetryService.trackUserLogout({});
        // Clear telemetry user context
        this.telemetryService.clearUserId();
        // Stop Notification Service
        this.notificationService.stop();
        this.activityInboxService.stop();
        this.api = null;
        // Clear all cached data to ensure fresh data for next user
        this.dataCache.clear();
        this.teammateCommunications = null;
        this.commMetrics.reset(false);
        this.metaLeadAds = null;
        this.metaLeadAdsLoad = null;
        this.teammateCommunicationsRequest = null;
        this.teammateCommunicationsRetryAtMs = 0;
        this.teammateCommunicationsSession++;
        this.logger.info('🗑️ Cache cleared during sign out');
        // Clear loading states
        this.loadingStates.clear();
        // Emit logout event
        this.eventEmitter.emit(AppEventType.USER_LOGGED_OUT, {
            timestamp: new Date()
        });
    }
    /**
     * Handle backend authentication with data fetch in a single request
     * Returns the combined login response with user data, workspace, jobs, and settings
     */
    async handleBackendAuthenticationWithData() {
        if (!this.api || !this.userRole) {
            const error = 'API or user role not available for backend authentication';
            this.logger.error(error);
            throw new Error(error);
        }
        this.logger.info(`🔐 Starting combined auth + data fetch for role: ${this.userRole}`);
        const includeData = this.loginDataTypes();
        try {
            // Try to login with data fetch
            this.logger.info('🔐 Attempting backend login with data prefetch...');
            const response = await this.makeApiCallWithRetry(() => this.api.loginUser(includeData, this.userRole, true, this.tabId));
            this.logger.info('✅ Backend login with data successful');
            return response;
        }
        catch (loginError) {
            // Sign-in is the only way in: a new user is added by it when the allowed-users list (or the
            // allow-all policy) admits them.
            this.logger.warn('❌ Backend login failed:', loginError);
            const currentUser = this.authService.getCurrentUser();
            const username = currentUser?.email || currentUser?.displayName || 'unknown user';
            throw new Error(`User "${username}" does not have access to this application. Please contact an administrator to request access.`);
        }
    }
    /** The data a sign-in reads, by role. Conversations load when they're opened (a page at a time). */
    loginDataTypes() {
        const includeData = ['userData', 'jobs', 'settings', 'candidates'];
        // Recruiter/Admin: also the organization, invitations, and the org's active scheduled messages
        // (they power the follow-up timeline).
        if (this.userRole === 'recruiter' || this.userRole === 'admin') {
            includeData.push('organization', 'invitations', 'scheduledMessages');
        }
        return includeData;
    }
    /**
     * Read the sign-in data again, without registering the tab a second time: live updates may have been
     * missed (the subscription lapsed, or the tab was away longer than notifications are kept). Rare.
     */
    async reloadData() {
        const role = this.userRole;
        const session = this.signInSession;
        if (!this.api || !role || this.activeSignInUid === null)
            return;
        const startTime = Date.now();
        const response = await this.makeApiCallWithRetry(() => this.api.loginUser(this.loginDataTypes(), role, false, this.tabId));
        // Signed out (and maybe in again) meanwhile: this data isn't this session's.
        if (this.signInSession !== session)
            return;
        this.processLoginDataResponse(response, startTime);
        // Updates to the report may have been missed too (or it's another workspace's): views load it again.
        this.commMetrics.reset(true);
        // Conversations aren't in the sign-in data: those loaded read their newest messages again.
        for (const chatId of this.dataCache.loadedChatIds()) {
            this.reloadChat(chatId).catch((error) => this.logger.warn(`Couldn't reload chat ${chatId}`, error));
        }
    }
    /** Read a loaded conversation's newest page again: after a load in flight, which may have read it too early. */
    async reloadChat(chatId) {
        await this.chatLoads.get(chatId)?.catch(() => undefined);
        await this.getChatMessages(chatId);
    }
    /**
     * Process the combined login response and emit data loaded events
     */
    processLoginDataResponse(response, fetchStartTime) {
        this.logger.info('📋 Processing combined login data response...');
        // Emit userData if present (workspace fields are now part of userData)
        if (response.userData) {
            this.dataCache.updateData({
                userData: response.userData,
                timestamp: Date.now()
            });
            this.eventEmitter.emit(AppEventType.USERDATA_UPDATED, {
                userData: response.userData,
                timestamp: new Date()
            });
            this.logger.info('📋 User data processed from login response');
            // Sync timezone: if browser timezone differs from settings, update settings
            this.syncTimezoneIfNeeded(response.userData);
        }
        // Cache organization + invitations if present (members are on org.members)
        if (response.organization) {
            this.dataCache.updateData({
                organization: response.organization,
                pendingInvitations: response.pendingInvitations,
                timestamp: Date.now()
            });
            this.eventEmitter.emit(AppEventType.ORG_UPDATED, {
                organization: response.organization,
                timestamp: new Date()
            });
            this.logger.info('📋 Organization data processed from login response');
        }
        // Emit jobs if present
        if (response.jobs) {
            this.dataCache.updateData({
                jobs: response.jobs,
                timestamp: Date.now()
            });
            const event = {
                jobs: response.jobs,
                timestamp: new Date()
            };
            this.eventEmitter.emit(AppEventType.JOBS_LOADED, event);
            this.logger.info(`📋 Jobs processed from login response: ${response.jobs.length} jobs`);
        }
        // Emit candidates if present
        if (response.candidates) {
            this.dataCache.updateData({
                candidates: response.candidates,
                timestamp: Date.now()
            });
            const event = {
                candidates: response.candidates,
                timestamp: new Date()
            };
            this.eventEmitter.emit(AppEventType.CANDIDATES_LOADED, event);
            this.logger.info(`📋 Candidates processed from login response: ${response.candidates.length} candidates`);
        }
        // Cache settings from login response
        if (response.settings) {
            this.dataCache.updateData({
                settings: response.settings,
                timestamp: Date.now()
            });
            this.logger.info('📋 Settings processed from login response');
        }
        // Hydrate the scheduled-messages cache (org-scoped, recent window).
        // After this, every follow-up timeline reads sync from the cache and
        // is patched by SCHEDULED_MESSAGE(S)_UPDATED notifications.
        if (response.scheduledMessages) {
            this.dataCache.updateData({
                scheduledMessages: response.scheduledMessages,
                timestamp: Date.now(),
            });
            this.logger.info(`📋 Scheduled messages processed from login response: ${response.scheduledMessages.length} messages`);
        }
        // Track telemetry for initial data load performance
        const durationMs = Date.now() - fetchStartTime;
        this.telemetryService.trackInitialDataLoaded({
            durationMs,
            jobsCount: response.jobs?.length || 0,
            candidatesCount: response.candidates?.length || 0
        });
        this.emitInitialDataLoaded();
        // Live updates held until now apply on top; older ones are already in this data.
        this.notificationService.markDataLoadedAt(response.serverTimeUtc);
        this.logger.info(`📋 Login data processing completed in ${durationMs}ms`);
    }
    /**
     * Compare browser timezone with workspace settings timezone.
     * If different, update settings and log the change.
     */
    syncTimezoneIfNeeded(userData) {
        const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const profileTz = userData.profile?.timezone;
        if (!browserTz || browserTz === profileTz)
            return;
        this.logger.info(`🕐 Timezone mismatch: profile="${profileTz}", browser="${browserTz}". Updating...`);
        this.updateProfile({ timezone: browserTz })
            .then(() => {
            this.logger.info(`🕐 Timezone updated to ${browserTz}`);
        })
            .catch((err) => {
            this.logger.error('Failed to sync timezone:', err);
        });
    }
    /**
     * Handle incoming notification
     */
    handleNotification(notification) {
        this.logger.info('🔔 Received notification:', notification);
        if (!notification || !notification.type)
            return;
        this.logger.info(`🔔 Processing notification [${notification.type}], ${JSON.stringify(notification)}`);
        switch (notification.type) {
            case NotificationType.JOB_CREATED: {
                const { job } = notification.payload;
                // The job may already be here: created by this tab, or read with the data during sign-in.
                const jobs = [...this.dataCache.getJobs().filter((existing) => existing.id !== job.id), job];
                this.dataCache.updateData({ jobs, timestamp: Date.now() });
                this.eventEmitter.emit(AppEventType.JOBS_LOADED, { jobs: [...jobs], timestamp: new Date() });
                this.logger.info(`Job ${job.id} added to cache via notification`);
                break;
            }
            case NotificationType.JOB_UPDATED: {
                const { job: updatedJob } = notification.payload;
                const currentJobs = [...this.dataCache.getJobs()];
                const idx = currentJobs.findIndex(j => j.id === updatedJob.id);
                if (idx >= 0) {
                    currentJobs[idx] = updatedJob;
                }
                else {
                    currentJobs.push(updatedJob);
                }
                this.dataCache.updateData({ jobs: currentJobs, timestamp: Date.now() });
                this.eventEmitter.emit(AppEventType.JOBS_LOADED, { jobs: currentJobs, timestamp: new Date() });
                this.logger.info(`Job ${updatedJob.id} updated in cache via notification`);
                break;
            }
            case NotificationType.JOB_IMPORT_COMPLETED:
            case NotificationType.JOB_OPTIMIZATION_COMPLETED:
                this.jobManager.refresh().catch(err => this.logger.error('Failed to refresh jobs on notification', err));
                break;
            case NotificationType.CANDIDATE_CREATED: {
                const { candidate: newCandidate } = notification.payload;
                this.dataCache.updateCandidate(newCandidate);
                this.eventEmitter.emit(AppEventType.CANDIDATE_CREATED, { candidate: newCandidate, timestamp: new Date() });
                this.logger.info(`New candidate ${newCandidate.id} added to cache via notification`);
                break;
            }
            case NotificationType.CANDIDATE_UPDATED: {
                const { candidateId, candidate: fullCandidate, jobId, application } = notification.payload;
                if (candidateId && jobId && application) {
                    // Per-application LWW: replace the cached application only if this
                    // snapshot is newer, so a stale/out-of-order delivery can't revert
                    // newer state. Undefined version (legacy) can't gate — apply it.
                    const cached = this.dataCache.getCandidate(candidateId);
                    if (!cached) {
                        this.logger.warn(`CANDIDATE_UPDATED for ${candidateId} not in cache — skipping`);
                        break;
                    }
                    const cachedTs = cached.applications?.[jobId]?.updatedAtUtc ?? 0;
                    if (application.updatedAtUtc !== undefined && application.updatedAtUtc <= cachedTs) {
                        this.logger.info(`⏭️ Dropping stale CANDIDATE_UPDATED for ${candidateId}/${jobId} (ts ${application.updatedAtUtc} ≤ ${cachedTs})`);
                        break;
                    }
                    const updated = {
                        ...cached,
                        applications: { ...cached.applications, [jobId]: application },
                    };
                    this.dataCache.updateCandidate(updated);
                    this.eventEmitter.emit(AppEventType.CANDIDATE_UPDATED, { candidate: updated, timestamp: new Date() });
                }
                else if (candidateId && fullCandidate) {
                    // Candidate-level change (rare, no jobId): take candidate scalars but
                    // keep the per-application LWW state already in cache.
                    const cached = this.dataCache.getCandidate(candidateId);
                    const merged = cached
                        ? { ...fullCandidate, applications: { ...fullCandidate.applications, ...cached.applications } }
                        : fullCandidate;
                    this.dataCache.updateCandidate(merged);
                    this.eventEmitter.emit(AppEventType.CANDIDATE_UPDATED, { candidate: merged, timestamp: new Date() });
                }
                else {
                    this.logger.warn('CANDIDATE_UPDATED notification with incomplete payload — skipping', notification.payload);
                }
                break;
            }
            case NotificationType.CANDIDATES_UPDATED: {
                // Bulk delta — patch each candidate in the cache and fan out
                // per-candidate UPDATED events so existing subscribers
                // (modals, lists) refresh without bespoke handling.
                const { candidates } = notification.payload;
                for (const updated of candidates) {
                    this.dataCache.updateCandidate(updated);
                    this.eventEmitter.emit(AppEventType.CANDIDATE_UPDATED, { candidate: updated, timestamp: new Date() });
                }
                this.logger.info(`Patched ${candidates.length} candidate(s) from CANDIDATES_UPDATED notification`);
                break;
            }
            case NotificationType.CANDIDATE_PROCESSING_ERROR: {
                const { candidateId: errorCandidateId, jobId: errorJobId, error: errorMessage, canRetryAtUtc } = notification.payload;
                if (errorCandidateId) {
                    const cached = this.dataCache.getCandidate(errorCandidateId);
                    if (cached && errorJobId && cached.applications[errorJobId]) {
                        const updated = {
                            ...cached,
                            applications: {
                                ...cached.applications,
                                [errorJobId]: {
                                    ...cached.applications[errorJobId],
                                    processingStage: {
                                        stage: 'failed', startedAtUtc: nowUtc(),
                                        canRetryAtUtc, error: errorMessage,
                                    },
                                },
                            },
                        };
                        this.dataCache.updateCandidate(updated);
                        this.eventEmitter.emit(AppEventType.CANDIDATE_UPDATED, { candidate: updated, timestamp: new Date() });
                    }
                    this.logger.warn(`Processing error for candidate ${errorCandidateId}: ${errorMessage}`);
                }
                break;
            }
            case NotificationType.SCORING_RETRY: {
                const retryPayload = notification.payload;
                this.eventEmitter.emit(AppEventType.SCORING_RETRY, {
                    candidateId: retryPayload.candidateId,
                    jobId: retryPayload.jobId,
                    attempt: retryPayload.attempt,
                    maxAttempts: retryPayload.maxAttempts,
                    delaySec: retryPayload.delaySec,
                    errorType: retryPayload.errorType,
                });
                break;
            }
            case NotificationType.CHAT_MESSAGE: {
                const { chatId, message } = notification.payload;
                if (chatId && message) {
                    this.chatChangesDuringLoad.get(chatId)?.add(message.id);
                    const cached = this.dataCache.getChatMessages(chatId);
                    const existingIndex = cached.findIndex(m => m.id === message.id);
                    let updated;
                    if (existingIndex >= 0) {
                        // Update only the delivery outcome (status, error, channel, re-sends) on the existing message
                        updated = cached.map((m, i) => i === existingIndex
                            ? {
                                ...m,
                                deliveryStatus: message.deliveryStatus,
                                deliveryError: message.deliveryError,
                                channel: message.channel ?? m.channel,
                                deliveredAtUtc: message.deliveredAtUtc,
                                deliveryRetry: message.deliveryRetry,
                            }
                            : m);
                    }
                    else {
                        updated = [...cached, message];
                    }
                    this.dataCache.setChatMessages(chatId, updated);
                    this.eventEmitter.emit(AppEventType.CHAT_MESSAGE_RECEIVED, { chatId, messages: updated, earlierLoaded: false });
                }
                break;
            }
            case NotificationType.INTERVIEW_SCHEDULED: {
                const { calendarEvent } = notification.payload;
                if (calendarEvent) {
                    this.logger.info('📅 CLIENT received calendar event:', {
                        eventId: calendarEvent.id,
                        startTimestamp: calendarEvent.startTimeUtc,
                        endTimestamp: calendarEvent.endTimeUtc,
                        startUtc: new Date(calendarEvent.startTimeUtc).toISOString(),
                        endUtc: new Date(calendarEvent.endTimeUtc).toISOString(),
                        startLocal: new Date(calendarEvent.startTimeUtc).toLocaleString(),
                        endLocal: new Date(calendarEvent.endTimeUtc).toLocaleString(),
                        browserTz: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    });
                    const userData = this.dataCache.getUserData();
                    // It's on the calendar of the job's recruiter: a teammate's interview isn't on mine.
                    const job = this.dataCache.getJobs().find((candidateJob) => candidateJob.id === notification.payload.jobId);
                    if (userData && job?.userId === userData.id) {
                        // The event may already be here: read with the data, or notified twice.
                        const updatedUserData = {
                            ...userData,
                            calendarEvents: [
                                ...userData.calendarEvents.filter((existing) => existing.id !== calendarEvent.id),
                                calendarEvent,
                            ],
                        };
                        this.dataCache.updateData({ userData: updatedUserData, timestamp: Date.now() });
                        this.eventEmitter.emit(AppEventType.USERDATA_UPDATED, { userData: updatedUserData, timestamp: new Date() });
                    }
                }
                break;
            }
            case NotificationType.SCHEDULED_MESSAGE_UPDATED: {
                // Patch local cache, then relay to subscribers. The denormalized
                // `JobApplication.followUpSummary` is refreshed separately via
                // CANDIDATE_UPDATED.
                const { message } = notification.payload;
                this.dataCache.upsertScheduledMessage(message);
                this.eventEmitter.emit(AppEventType.SCHEDULED_MESSAGE_UPDATED, { message });
                this.logger.info(`Scheduled message ${message.id} updated → ${message.status}`);
                break;
            }
            case NotificationType.SCHEDULED_MESSAGES_UPDATED: {
                // Bulk variant — patch and emit ONE event so subscribers
                // re-derive their slice in a single render cycle.
                const { messages } = notification.payload;
                if (messages.length === 0)
                    break;
                this.dataCache.upsertScheduledMessages(messages);
                const jobIds = Array.from(new Set(messages.map((m) => m.jobId)));
                this.eventEmitter.emit(AppEventType.SCHEDULED_MESSAGES_UPDATED, { messages, jobIds });
                this.logger.info(`Scheduled messages bulk update: count=${messages.length}`);
                break;
            }
            case NotificationType.SCHEDULED_MESSAGE_DELETED: {
                // Admin hard-delete — drop from cache and tell the UI.
                const { id, candidateId, jobId } = notification.payload;
                this.dataCache.removeScheduledMessage(id);
                this.eventEmitter.emit(AppEventType.SCHEDULED_MESSAGE_DELETED, { id, candidateId, jobId });
                this.logger.info(`Scheduled message ${id} hard-deleted by admin`);
                break;
            }
            case NotificationType.JOB_DELETED: {
                const { jobId } = notification.payload;
                const currentJobs = this.dataCache.getJobs().filter(j => j.id !== jobId);
                this.dataCache.updateData({ jobs: currentJobs, timestamp: Date.now() });
                this.eventEmitter.emit(AppEventType.JOBS_LOADED, { jobs: currentJobs, timestamp: new Date() });
                this.logger.info(`Job ${jobId} removed from cache via notification`);
                break;
            }
            case NotificationType.CANDIDATE_DELETED: {
                const { candidateIds } = notification.payload;
                const idSet = new Set(candidateIds);
                const remaining = this.dataCache.getCandidates().filter(c => !idSet.has(c.id));
                this.dataCache.updateData({ candidates: remaining, timestamp: Date.now() });
                this.eventEmitter.emit(AppEventType.CANDIDATES_LOADED, { candidates: remaining, timestamp: new Date() });
                this.logger.info(`${candidateIds.length} candidate(s) removed from cache via notification`);
                break;
            }
            case NotificationType.COMM_METRICS_UPDATED: {
                if (notification.payload.orgId === this.dataCache.getOrganization()?.id) {
                    this.commMetrics.add(notification.payload);
                }
                break;
            }
            case NotificationType.ORG_UPDATED: {
                const { subscription, usage } = notification.payload;
                const currentOrg = this.dataCache.getOrganization();
                if (currentOrg) {
                    const updatedOrg = { ...currentOrg, subscription, usage };
                    this.dataCache.updateData({ organization: updatedOrg, timestamp: Date.now() });
                    this.eventEmitter.emit(AppEventType.ORG_UPDATED, { organization: updatedOrg, timestamp: new Date() });
                    this.logger.info('Organization subscription/usage updated via notification');
                }
                break;
            }
            case NotificationType.USER_UPDATED: {
                // Per-user profile push (activePreview flips, verifiedPhone, etc.).
                // Patch local userData.profile and emit USERDATA_UPDATED so any
                // profile-driven UI (banners, button gates) re-renders.
                const { profile } = notification.payload;
                const currentUserData = this.dataCache.getUserData();
                if (currentUserData) {
                    // Moved to another workspace (joined a team, removed from one): its data replaces this one's.
                    if (profile.orgId !== currentUserData.profile.orgId) {
                        this.notificationService.reload('moved to another workspace');
                    }
                    const updatedUserData = { ...currentUserData, profile };
                    this.dataCache.updateData({ userData: updatedUserData, timestamp: Date.now() });
                    this.eventEmitter.emit(AppEventType.USERDATA_UPDATED, { userData: updatedUserData, timestamp: new Date() });
                    this.logger.info('User profile updated via notification');
                }
                break;
            }
        }
    }
    /**
     * Wrapper for API calls that handles token expiration and retry logic.
     * Bounded to **one** retry: if the second attempt also returns 401, we
     * treat it as a real auth failure, emit `USER_LOGGED_OUT`, and let the
     * auth state listener redirect to login. The "Token expired" string is
     * never surfaced to the UI \u2014 silent refresh is the only user-visible
     * outcome on the happy path.
     */
    async makeApiCallWithRetry(apiCall) {
        try {
            return await apiCall();
        }
        catch (error) {
            if (!this.isTokenError(error)) {
                throw error;
            }
            this.logger.info('🔄 Detected token expiration, attempting silent refresh...');
            try {
                // AuthService deduplicates parallel refreshes via an in-flight promise.
                const newToken = await this.authService.refreshToken();
                if (this.api)
                    this.api.updateToken(newToken);
                this.logger.info('✅ Token refreshed, retrying API call');
            }
            catch (refreshError) {
                this.logger.error('❌ Token refresh failed; signing user out', refreshError);
                this.eventEmitter.emit(AppEventType.USER_LOGGED_OUT, { timestamp: new Date() });
                throw refreshError;
            }
            try {
                // Single retry. If this also 401s, the token was revoked/invalid
                // server-side after refresh \u2014 escalate to logout.
                return await apiCall();
            }
            catch (retryError) {
                if (this.isTokenError(retryError)) {
                    this.logger.error('❌ Retry after refresh also returned auth error; signing user out');
                    this.eventEmitter.emit(AppEventType.USER_LOGGED_OUT, { timestamp: new Date() });
                }
                throw retryError;
            }
        }
    }
    /** True iff the error is a 401-class token problem (expired/revoked/invalid).
     *  403 (authorization) is intentionally excluded \u2014 those should propagate. */
    isTokenError(error) {
        const msg = error instanceof Error ? error.message : String(error);
        // Server returns `code: 'TOKEN_EXPIRED' | 'TOKEN_REVOKED' | 'INVALID_TOKEN'`
        // alongside HTTP 401. Match either signal.
        return (msg.includes('401') ||
            msg.includes('TOKEN_EXPIRED') ||
            msg.includes('TOKEN_REVOKED') ||
            msg.includes('INVALID_TOKEN') ||
            msg.includes('Token expired') ||
            msg.includes('Token revoked') ||
            msg.includes('Invalid token'));
    }
    /**
     * Ensure API is available, throw error if not
     */
    ensureAuthenticated() {
        if (!this.api || !this.isAuthenticated()) {
            throw new Error('User must be authenticated to perform this action. Please call login() first.');
        }
    }
    /**
     * Convert unknown error to Error instance safely
     */
    toError(error) {
        if (error instanceof Error) {
            return error;
        }
        return new Error(String(error));
    }
    /**
     * Handle errors and emit error events
     */
    handleError(context, error) {
        const errorMessage = error.message;
        this.logger.error(`Error in ${context}:`, errorMessage);
        // Track error telemetry
        this.telemetryService.trackError({
            context,
            errorMessage
        });
        const errorEvent = {
            error,
            context,
            timestamp: new Date()
        };
        this.eventEmitter.emit(AppEventType.ERROR_OCCURRED, errorEvent);
    }
    /**
     * Emit initial data loaded event (after login data is fully loaded)
     */
    emitInitialDataLoaded() {
        const event = {
            timestamp: new Date()
        };
        this.eventEmitter.emit(AppEventType.INITIAL_DATA_LOADED, event);
    }
    /**
     * Subscribe to auth state changes
     */
    onAuthStateChanged(callback) {
        return this.authService.onAuthStateChanged(callback);
    }
    /**
     * Submit user feedback
     */
    async submitFeedback(feedbackData) {
        this.logger.info('📝 Submitting user feedback');
        try {
            const result = await this.makeApiCallWithRetry(() => this.api.submitFeedback(feedbackData));
            this.logger.info('✅ Feedback submitted successfully:', result.feedbackId);
            return result;
        }
        catch (error) {
            this.logger.error('❌ Failed to submit feedback:', error);
            throw error;
        }
    }
    async updateCandidateScore(candidateId, jobId, newScore, reason, newRecommendation) {
        return this.candidateManager.updateScore(candidateId, jobId, newScore, reason, newRecommendation);
    }
    async updateCandidateHiringStatus(candidateId, jobId, status, remark) {
        return this.candidateManager.updateHiringStatus(candidateId, jobId, status, remark);
    }
    async moveCandidateStage(candidateId, jobId, stageId) {
        return this.candidateManager.moveCandidateStage(candidateId, jobId, stageId);
    }
    async setCandidateState(candidateId, jobId, state) {
        return this.candidateManager.setCandidateState(candidateId, jobId, state);
    }
    async updateCandidateTags(candidateId, jobId, tags) {
        return this.candidateManager.updateTags(candidateId, jobId, tags);
    }
    async updateCandidateQuestions(candidateId, jobId, questions) {
        return this.candidateManager.updateQuestions(candidateId, jobId, questions);
    }
    async resetCandidateQuestionsToJob(candidateId, jobId) {
        return this.candidateManager.resetQuestionsToJob(candidateId, jobId);
    }
    /**
     * Track pricing page view event
     */
    trackPricingPageView(event) {
        this.telemetryService.trackPricingPageView(event);
    }
}
